import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, Platform } from 'react-native';
import { StyleSheet } from 'react-native';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { useNavigation } from '@react-navigation/native';
import { globalStyles } from '../styles/globalStyles';
import { getResponsiveValue } from '../styles/breakpoints';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, addDoc } from 'firebase/firestore';

export function Contact() {
  const navigation = useNavigation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    messageType: 'General Inquiry',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const messageTypes = [
    'General Inquiry',
    'Request Venue Addition',
    'Promotional',
    'Other'
  ];

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Send email
      const functions = getFunctions();
      const handleEmail = httpsCallable(functions, 'handleEmail');
      
      await handleEmail({
        template: 'contact_form',
        recipient: 'contact@venuereportcard.com',
        emailData: formData
      });

      // Store in Firestore
      const db = getFirestore();
      await addDoc(collection(db, 'contact_submissions'), {
        ...formData,
        createdAt: new Date().toISOString(),
        emailSent: true
      });

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        messageType: 'General Inquiry',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <View style={globalStyles.container}>
      <BackgroundVideo />
      <View style={globalStyles.overlay}>
        <ScrollView style={styles.scrollContainer}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.backButtonText}>← Back</Text>
          </TouchableOpacity>

          <Text style={styles.title}>CONTACT US</Text>
          
          <View style={styles.formContainer}>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Name</Text>
              <TextInput
                style={styles.input}
                value={formData.name}
                onChangeText={(text) => setFormData({ ...formData, name: text })}
                placeholder="Your name"
                placeholderTextColor="#666"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Email</Text>
              <TextInput
                style={styles.input}
                value={formData.email}
                onChangeText={(text) => setFormData({ ...formData, email: text })}
                placeholder="Your email"
                placeholderTextColor="#666"
                keyboardType="email-address"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Message Type</Text>
              <select
                style={styles.select}
                value={formData.messageType}
                onChange={(e) => setFormData({ ...formData, messageType: e.target.value })}
              >
                {messageTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Message</Text>
              <TextInput
                style={[styles.input, styles.messageInput]}
                value={formData.message}
                onChangeText={(text) => setFormData({ ...formData, message: text })}
                placeholder="Your message"
                placeholderTextColor="#666"
                multiline
                numberOfLines={6}
                textAlignVertical="top"
              />
            </View>

            <TouchableOpacity
              style={[
                styles.submitButton,
                isSubmitting && styles.submitButtonDisabled
              ]}
              onPress={handleSubmit}
              disabled={isSubmitting}
            >
              <Text style={styles.submitButtonText}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Text>
            </TouchableOpacity>

            {submitStatus === 'success' && (
              <Text style={styles.successText}>
                Thank you for your message! We'll get back to you soon.
              </Text>
            )}
            {submitStatus === 'error' && (
              <Text style={styles.errorText}>
                There was an error sending your message. Please try again.
              </Text>
            )}
          </View>
        </ScrollView>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  scrollContainer: {
    width: '100%',
    maxWidth: 1200,
    alignSelf: 'center',
    padding: 20,
  },
  backButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    padding: 10,
    alignItems: 'center',
    marginBottom: 20,
    alignSelf: 'flex-start',
    minWidth: 100,
  },
  backButtonText: {
    color: '#000000',
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
  },
  title: {
    fontFamily: 'Playpen Sans',
    fontSize: 48,
    color: '#000000',
    marginBottom: 30,
    textAlign: 'center',
    textShadow: '0px 2px 4px rgba(0,0,0,0.25)',
  },
  formContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 10,
    padding: getResponsiveValue({
      mobile: 20,
      tablet: 30,
      laptop: 40,
      desktop: 40
    }),
    width: '100%',
    maxWidth: 800,
    alignSelf: 'center',
    marginBottom: 40,
  },
  inputGroup: {
    marginBottom: 20,
  },
  label: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    color: '#000000',
    marginBottom: 8,
  },
  input: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 8,
    padding: 12,
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    color: '#000000',
    width: '100%',
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.1)',
  },
  messageInput: {
    minHeight: 150,
    textAlignVertical: 'top',
  },
  select: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 8,
    padding: 12,
    fontFamily: 'Arial',
    fontSize: 16,
    color: '#000000',
    width: '100%',
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.1)',
    height: 45,
    cursor: 'pointer',
    outlineStyle: 'none',
    paddingLeft: 12,
    paddingRight: 36,
  },
  submitButton: {
    backgroundColor: '#2ecc71',
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
    marginTop: 20,
  },
  submitButtonDisabled: {
    opacity: 0.7,
  },
  submitButtonText: {
    color: '#ffffff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    fontWeight: '500',
  },
  successText: {
    color: '#2ecc71',
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    textAlign: 'center',
    marginTop: 20,
  },
  errorText: {
    color: '#e74c3c',
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    textAlign: 'center',
    marginTop: 20,
  },
}); 